import React, { useState } from 'react';
import { openPicker } from '../../../utils/timePicker';
import { X, Trash2 } from 'lucide-react';
import { CATS, CAT_IDS, GOLD, GOLD_HI, SUGGESTIONS } from './agendaStore';

/* Bottom sheet: forge (add) or edit an agenda item. */
export const AgendaForge = ({ items, editing, onSave, onDelete, onClose }) => {
  const [name, setName] = useState(editing?.name || '');
  const [time, setTime] = useState(editing?.time || '');
  const [cat, setCat] = useState(editing?.cat || 'fashion');
  const existing = new Set(items.map((i) => i.name.toLowerCase()));

  const submit = () => {
    const n = name.trim();
    if (!n) return;
    onSave({ ...(editing || {}), name: n, time, cat });
    if (!editing) { setName(''); setTime(''); }
  };

  return (
    <div className="fixed inset-0 z-[240] flex items-end" data-testid="agenda-forge-sheet">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" data-testid="agenda-forge-backdrop"/>
      <div className="relative w-full rounded-t-3xl px-5 pt-4 pb-6 max-h-[88vh] overflow-y-auto"
        style={{ background: '#0c0a06', borderTop: `1px solid ${GOLD}`, paddingBottom: 'max(24px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-['Cinzel'] uppercase tracking-[0.26em]" style={{ color: GOLD_HI, fontSize: 13 }}>
            {editing ? 'Edit Item' : 'Forge Your Agenda'}
          </span>
          <button onClick={onClose} data-testid="agenda-forge-close" className="p-1.5 rounded-full active:scale-90"
            style={{ border: '1px solid rgba(212,175,55,0.4)' }}><X size={16} color={GOLD_HI}/></button>
        </div>

        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Wake up at 05:00"
          maxLength={40} data-testid="agenda-forge-name"
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          className="w-full rounded-xl px-4 py-3 bg-transparent text-white font-['Outfit'] outline-none"
          style={{ border: '1px solid rgba(212,175,55,0.4)', fontSize: 15 }}/>

        <div className="flex items-center gap-3 mt-3">
          <span className="font-['Outfit'] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>Time (optional)</span>
          <input type="time" onClick={(e) => openPicker(e.currentTarget)} value={time} onChange={(e) => setTime(e.target.value)} data-testid="agenda-forge-time"
            className="flex-1 rounded-lg px-3 py-2 bg-transparent text-white font-['Outfit'] outline-none"
            style={{ border: '1px solid rgba(212,175,55,0.3)', colorScheme: 'dark' }}/>
        </div>

        <div className="mt-4 font-['Outfit'] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>Pillar</div>
        <div className="grid grid-cols-4 gap-2 mt-2">
          {CAT_IDS.map((id) => {
            const c = CATS[id]; const on = cat === id;
            return (
              <button key={id} onClick={() => setCat(id)} data-testid={`agenda-forge-cat-${id}`}
                className="flex flex-col items-center gap-1 py-2 rounded-xl active:scale-95"
                style={{ background: on ? `${c.color}22` : 'transparent', border: `1px solid ${on ? c.color : 'rgba(255,255,255,0.1)'}`,
                  transition: 'background-color 0.12s, border-color 0.12s' }}>
                <img src={c.icon} alt="" className="w-7 h-7 object-contain"/>
                <span className="font-['Outfit']" style={{ fontSize: 9.5, color: on ? c.color : 'rgba(255,255,255,0.6)' }}>{c.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mt-5">
          {editing && (
            <button onClick={() => onDelete(editing.id)} data-testid="agenda-forge-delete"
              className="px-4 rounded-full flex items-center gap-1 font-['Outfit'] uppercase active:scale-95"
              style={{ border: '1px solid #FF4A6A', color: '#FF4A6A', fontSize: 11 }}><Trash2 size={13}/> Delete</button>
          )}
          <button onClick={submit} disabled={!name.trim()} data-testid="agenda-forge-save"
            className="flex-1 py-3 rounded-full font-['Cinzel'] uppercase tracking-[0.22em] active:scale-[0.98] disabled:opacity-40"
            style={{ background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0806', fontSize: 12, fontWeight: 700 }}>
            {editing ? 'Save' : 'Forge Item'}
          </button>
        </div>

        {!editing && (
          <>
            <div className="mt-5 font-['Outfit'] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>Quick add</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {SUGGESTIONS.filter(([n]) => !existing.has(n.toLowerCase())).map(([n, c]) => (
                <button key={n} onClick={() => onSave({ name: n, time: '', cat: c })} data-testid={`agenda-suggest-${n.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full font-['Outfit'] active:scale-95"
                  style={{ border: `1px solid ${CATS[c].color}66`, color: CATS[c].color, fontSize: 11 }}>+ {n}</button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
